import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { Trans, useTranslation } from 'next-i18next';
import type { SchoolSearchRes } from '@/api/gen/_model/schoolSearchRes';
import { ExclamationmarkSmallCircleColorIcon } from '@/icons';
import { SignSelectSchoolGrade } from '../../form';
import { useSetSchoolTypeAndGradeCode } from '../../hook';
import { SchoolSearchInput, SchoolSearchManualInput } from './SchoolSearchInput';
import { type SchoolFormData } from './SchoolSearchModal';
import { SchoolSearchResultArea } from './SchoolSearchResultArea';
interface SchoolSearchModalBodyProps {
  selectedSchool: SchoolSearchRes | null;
  setSelectedSchool: (school: SchoolSearchRes | null) => void;
  isManualMode: boolean;
  setIsManualMode: (value: boolean) => void;
}
export const SchoolSearchModalBody = ({
  selectedSchool,
  setSelectedSchool,
  isManualMode,
  setIsManualMode
}: SchoolSearchModalBodyProps) => {
  const {
    control,
    setValue
  } = useFormContext<SchoolFormData>();
  const [submittedQuery, setSubmittedQuery] = useState('');
  useSetSchoolTypeAndGradeCode();
  const handleSearch = (searchQuery: string) => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) return;
    setSubmittedQuery(trimmedQuery);
    setSelectedSchool(null);
    setValue('schoolGrade', '', {
      shouldValidate: true
    });
  };
  const handleSelectSchool = (school: SchoolSearchRes) => {
    setSelectedSchool(school);
    setValue('schoolName', school.name, {
      shouldValidate: true
    });
    setValue('schoolType', school.schoolType, {
      shouldValidate: true
    });
    setValue('verifiedSchool', {
      standardCode: school.standardCode
    }, {
      shouldValidate: true
    });
  };
  if (isManualMode) {
    return <SchoolSearchManualInput />;
  }
  return <div className="flex flex-col gap-md px-lg" data-sentry-component="SchoolSearchModalBody" data-sentry-source-file="SchoolSearchModalBody.tsx">
      <div className="flex flex-col gap-xs">
        <SchoolSearchInput onSearch={handleSearch} isSearched={!!selectedSchool} data-sentry-element="SchoolSearchInput" data-sentry-source-file="SchoolSearchModalBody.tsx" />
        {selectedSchool && <Controller name="schoolGrade" control={control} defaultValue="" render={({
        field
      }) => <SignSelectSchoolGrade value={field.value} onValueChange={field.onChange} />} />}
      </div>
      {!selectedSchool && <div className="flex flex-col gap-xs py-xs">
          {submittedQuery && <SchoolSearchResultArea submittedQuery={submittedQuery} onSelectSchool={handleSelectSchool} />}
          <NoSchoolNotice onClick={() => setIsManualMode(true)} />
        </div>}
    </div>;
};
const NoSchoolNotice = ({
  onClick
}: {
  onClick: () => void;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <div onClick={onClick} className="mb-lg flex cursor-pointer justify-center rounded-sm bg-neutral py-sm text-caption-lg-medium text-neutral hover:bg-gray-100" data-sentry-component="NoSchoolNotice" data-sentry-source-file="SchoolSearchModalBody.tsx">
      <ExclamationmarkSmallCircleColorIcon size="xs" fill={ICON_COLOR.destructive} className="mr-xs" data-sentry-element="ExclamationmarkSmallCircleColorIcon" data-sentry-source-file="SchoolSearchModalBody.tsx" />
      <Trans t={t} i18nKey={'account:signup.school.searchModalCaution' as never} components={{
      strong: <span className="text-caption-lg-semibold text-destructive" />
    }} data-sentry-element="Trans" data-sentry-source-file="SchoolSearchModalBody.tsx" />
    </div>;
};